import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PayPalButton } from 'react-paypal-button-v2';

import { useConfig } from '../../../config/ConfigProvider';
import { initiatePaypalPayment } from '../../../services/paymentService';
import { captureException } from '../../../services/sentryService';
import { getAdobeAccountEventTracking } from '../../../services/trackingService';
import { useAppContext } from '../../AppContextProvider';
import Notification from '../notification/Notification';
import { useRetryPaymentContext } from '../RetryPaymentContextProvider';

import RetryPaymentLoader from './RetryPaymentLoader';
import styles from './RetryPaymentPaypal.module.scss';

export type RetryPaymentPaypalProps = {
  currency: string;
  amount: number;
  orderId: string;
  siteId: number;
};

const RetryPaymentPaypal: React.FC<RetryPaymentPaypalProps> = ({
  currency,
  amount,
  orderId,
  siteId,
}) => {
  const { csrfToken } = useAppContext();
  const { setRetryPaymentInfo } = useRetryPaymentContext();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const {
    config: {
      paypal: { clientId },
    },
  } = useConfig();

  const sendAdobeTrackingClickEvent = () => {
    const adbAccEvent = getAdobeAccountEventTracking();
    adbAccEvent.track('retry_payment', {
      payment_method: 'PAYPAL',
      order_id: orderId,
    });
  };

  return (
    <div className={styles.retryPaypalPayment}>
      {loading && <RetryPaymentLoader />}

      <Notification
        id="retryPaymentNotification"
        ariaLabel={t('mzp.order.detail.retry.payment.accessibility.label')}
        type="warning">
        <p>
          {t('mzp.order.detail.payment.reminder.warning', {
            0: t('mzp.order.detail.retry.payment.button', {
              0: t('general.payment.paypal'),
            }),
          })}
        </p>
        <div
          className={styles.retryPaypalPaymentButtonContainer}
          data-zta="retryPaypalPaymentContainer">
          <PayPalButton
            style={{ tagline: false, layout: 'horizontal', height: 36 }}
            options={{
              clientId,
              currency,
              intent: 'authorize',
            }}
            createOrder={(data: any, actions: any) => {
              sendAdobeTrackingClickEvent();
              return actions.order.create({
                purchase_units: [
                  {
                    amount: {
                      currency_code: currency,
                      value: amount,
                    },
                  },
                ],
              });
            }}
            onApprove={async (data: any) => {
              setLoading(true);
              try {
                const response = await initiatePaypalPayment({
                  csrfToken: csrfToken as string,
                  tokenId: data.orderID,
                  paymentAmount: amount,
                  orderId,
                  siteId,
                  currency,
                });
                const retryPaymentStatus = response.data;
                const retryPossible =
                  retryPaymentStatus === 'CANCELLED' || retryPaymentStatus === 'UNSUCCESSFUL';
                setRetryPaymentInfo({ retryPossible, retryPaymentStatus });
              } catch (e: any) {
                captureException(e);
                setRetryPaymentInfo({ retryPossible: false, retryPaymentStatus: 'UNKNOWN' });
              } finally {
                setLoading(false);
              }
            }}
            onError={(error: any) => {
              captureException({ error, message: 'Error received from Paypal button' });
              setLoading(false);
              setRetryPaymentInfo({ retryPossible: false, retryPaymentStatus: 'CANCELLED' });
            }}
          />
        </div>
      </Notification>
    </div>
  );
};

export default RetryPaymentPaypal;
